import { contestantUpdates, type ContestantUpdate, type PortraitCrop } from './contestant-updates.ts'

// All 98 numbered nicknames checked against the 49-page live Canva on 25 September 2026.
// Keep the user's explicit disambiguation of 35/55; the original poster also spells 19 สุนิศา.
export const contestantNames = [
  'น้ำแป้ง', 'Barbeer', 'เก่ง', 'ทราย', 'ฟ้า', 'พลอย', 'พลอย', 'บี', 'นีน่า',
  'เบน', 'เอ๋', 'ก้อย', 'ฟ้า', 'Aura', 'นุช', 'นิด', 'ธันย์', 'แจน',
  'สุนิศา', 'แอร์', 'Alisa', 'Beauty', 'Buddy', 'แอน', 'กิ่ง', 'จั๊มเปอร์', 'นิ',
  'กิ๊บ', 'ว.', 'หยก', 'แอรี่', 'ดีดี้', 'รีน่า', 'อ้อ', 'ยุ้ย อภิญญา', 'กิ๊บ',
  ...contestantUpdates.filter(person => person.number >= 37).map(person => person.name),
] as const

export type PosterCrop = { x: number; y: number; width: number; height: number }
export type ContestantSlot = { kind: 'contestant'; number: number; name: string } & (
  { poster: number; crop: PosterCrop } | ContestantUpdate
)
export type RosterSlot = ContestantSlot | { kind: 'waiting'; key: string }

export const rosterPageSize = 9

export const posterSource = (page: number) => `/images/contestants/poster-${String(page).padStart(2, '0')}-lossless.webp`

export function contestantSlot(number: number): ContestantSlot {
  if (number < 1 || number > contestantNames.length || !Number.isInteger(number)) throw new RangeError('Unknown contestant')
  const update = contestantUpdates.find(person => person.number === number)
  if (update) return { kind: 'contestant', ...update }
  const index = (number - 1) % 9
  // Windows into the complete, unmodified source. Keep the original names,
  // professions, number badges and photographs together, without retyping them.
  const crop = { x: [112, 770, 1427][index % 3], y: [680, 1428, 2176][Math.floor(index / 3)], width: 620, height: 713 }
  return { kind: 'contestant', number, name: contestantNames[number - 1], poster: Math.ceil(number / 9), crop }
}

export function paginateRoster(people: ContestantSlot[]): RosterSlot[][] {
  return Array.from({ length: Math.ceil(people.length / rosterPageSize) }, (_, page) =>
    Array.from({ length: rosterPageSize }, (_, index) =>
      people[page * rosterPageSize + index] ?? { kind: 'waiting', key: `waiting-${page}-${index}` }))
}

export const rosterPages = paginateRoster(Array.from({ length: contestantNames.length }, (_, index) => contestantSlot(index + 1)))
export const rosterWaitingCount = rosterPages.flat().filter(slot => slot.kind === 'waiting').length

export const rosterPageLabels = rosterPages.map(page => {
  const people = page.filter((slot): slot is ContestantSlot => slot.kind === 'contestant')
  const first = String(people[0].number).padStart(2, '0')
  const last = String(people[people.length - 1].number).padStart(2, '0')
  return first === last ? first : `${first}–${last}`
})

export function clampRosterPage(page: number) {
  return Math.max(0, Math.min(rosterPages.length - 1, page))
}

export function posterWindowStyle(crop: PosterCrop) {
  return { width: `${2160 / crop.width * 100}%`, left: `${-crop.x / crop.width * 100}%`, top: `${-crop.y / crop.height * 100}%` }
}

export function portraitWindowStyle(crop: PortraitCrop) {
  return { width: `${crop.sourceWidth / crop.width * 100}%`, left: `${-crop.x / crop.width * 100}%`, top: `${-crop.y / crop.height * 100}%` }
}

// Fit the whole verified portrait window inside the fixed photo cell. Limiting
// only its width would clip the lower face of tall windows at the caption edge.
export function portraitFrameStyle(crop: PortraitCrop) {
  return { aspectRatio: `${crop.width} / ${crop.height}`, width: `min(100%, ${crop.width / crop.height * 100}cqh)` }
}

// Presentation-only windows remove phone chrome from supplied screenshots;
// original source files and the audited organizer records remain untouched.
const screenshotPortraits: Record<number, PortraitCrop> = {
  23: { x: 0, y: 307, width: 521, height: 518, sourceWidth: 521, sourceHeight: 1132 },
  71: { x: 0, y: 120, width: 432, height: 680, sourceWidth: 432, sourceHeight: 960 },
}

export function portraitDisplayCrop(slot: RosterSlot) {
  return slot.kind === 'contestant' && 'photo' in slot ? slot.photoCrop ?? screenshotPortraits[slot.number] : undefined
}
